import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Users, ShieldCheck, History, AlertTriangle, Loader2, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const CONF_STYLES = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

const FIELD_LABELS = { name: 'Name', email: 'Email', phone: 'Phone', address: 'Address' };

const CustomerColumn = ({ c }) => (
  <div className="border rounded-md p-3 text-sm space-y-1 min-w-0">
    <p className="font-semibold truncate" data-testid={`dup-customer-name-${c.id}`}>{c.name || '—'}</p>
    <p className="text-xs text-muted-foreground truncate">{c.email || 'no email'}</p>
    <p className="text-xs text-muted-foreground truncate">{c.phone || 'no phone'}</p>
    <p className="text-xs text-muted-foreground truncate">{c.address || 'no address'}</p>
    <p className="text-xs text-muted-foreground">Source: {c.source || '—'} · Created {c.created_at ? c.created_at.slice(0, 10) : '—'}</p>
    <div className="flex flex-wrap gap-1 pt-1">
      <Badge variant="outline" className="text-[10px]">{c.embedded_jobs} jobs</Badge>
      <Badge variant="outline" className="text-[10px]">{c.linked_records.enquiries} enquiries</Badge>
      <Badge variant="outline" className="text-[10px]">{c.linked_records.invoices} invoices</Badge>
      <Badge variant="outline" className="text-[10px]">{c.linked_records.payments} payments</Badge>
      <Badge variant="outline" className="text-[10px]">{c.linked_records.blocked_days} calendar</Badge>
    </div>
  </div>
);

export const PossibleDuplicatesPanel = ({ authHeaders, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const [mergeGroup, setMergeGroup] = useState(null);
  const [survivorId, setSurvivorId] = useState(null);
  const [includedIds, setIncludedIds] = useState([]);
  const [choices, setChoices] = useState({});
  const [preserveAlternates, setPreserveAlternates] = useState(true);
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const [rollbackPlan, setRollbackPlan] = useState(null);
  const [rollbackConfirm, setRollbackConfirm] = useState('');

  const fetchScan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customer-duplicates`, authHeaders);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate scan failed');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/customer-duplicates/merge-history`, authHeaders);
      setHistory(res.data.merges || []);
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchScan(); }, [fetchScan]);
  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory, fetchHistory]);

  const handleIgnore = async (pair) => {
    if (!execEnabled) {
      toast.error('Merge execution is disabled on this environment — decisions cannot be saved.');
      return;
    }
    try {
      await axios.post(`${API}/admin/customer-duplicates/ignore`, {
        customer_id_a: pair.ids[0], customer_id_b: pair.ids[1],
      }, authHeaders);
      toast.success('Marked as not a duplicate — it will stay hidden unless either record changes');
      fetchScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save decision');
    }
  };

  const openMerge = (group) => {
    setMergeGroup(group);
    setSurvivorId(group.customers[0].id);
    setIncludedIds(group.customers.map(c => c.id));
    setChoices({});
    setPreserveAlternates(true);
    setPreview(null);
    setConfirmText('');
  };

  const invalidatePreview = () => { setPreview(null); setConfirmText(''); };

  const selection = () => ({
    survivor_id: survivorId,
    duplicate_ids: mergeGroup.customers.filter(c => c.id !== survivorId).map(c => c.id),
    name: choices.name ?? null,
    email: choices.email ?? null,
    phone: choices.phone ?? null,
    address: choices.address ?? null,
    preserve_alternates: preserveAlternates,
  });

  const generatePreview = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/customer-duplicates/preview`, selection(), authHeaders);
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const executeMerge = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/customer-duplicates/merge`, {
        ...selection(),
        preview_fingerprint: preview.preview_fingerprint,
        confirmation: confirmText,
      }, authHeaders);
      toast.success(`Merged — ${res.data.retired_ids.length} record(s) retired (reversible from Merge History)`);
      setMergeGroup(null);
      fetchScan();
      if (onRefresh) onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Merge failed');
      if (e.response?.status === 409) invalidatePreview();
    } finally {
      setBusy(false);
    }
  };

  const openRollback = async (mergeId) => {
    try {
      const res = await axios.post(`${API}/admin/customer-duplicates/rollback-preview`, { merge_id: mergeId }, authHeaders);
      setRollbackPlan(res.data);
      setRollbackConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rollback preview failed');
    }
  };

  const executeRollback = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/customer-duplicates/rollback`, {
        merge_id: rollbackPlan.merge_id, confirmation: rollbackConfirm,
      }, authHeaders);
      toast.success('Merge rolled back — customers restored');
      setRollbackPlan(null);
      fetchScan();
      fetchHistory();
      if (onRefresh) onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Rollback failed'));
    } finally {
      setBusy(false);
    }
  };

  const groupCount = data?.group_count ?? 0;
  const execEnabled = data?.merge_execution_enabled === true;
  const distinctValues = (field) => mergeGroup
    ? [...new Set(mergeGroup.customers.filter(c => includedIds.includes(c.id)).map(c => (c[field] || '').trim()).filter(Boolean))]
    : [];

  return (
    <Card className="mb-4">
      <CardHeader className="p-3 sm:p-4 cursor-pointer" onClick={() => setOpen(o => !o)} data-testid="possible-duplicates-toggle">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Possible Duplicates
            {!loading && (
              <Badge variant={groupCount > 0 ? 'destructive' : 'secondary'} data-testid="duplicate-group-count">
                {groupCount} group{groupCount === 1 ? '' : 's'}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setShowHistory(s => !s); setOpen(true); }} data-testid="merge-history-btn" aria-label="Show merge history">
              <History className="h-4 w-4 mr-1" /> History
            </Button>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>
        {!loading && data && (
          <p className="text-xs text-muted-foreground">
            Scanned {data.scanned_customers} customers — {data.by_confidence.high} high, {data.by_confidence.medium} medium, {data.by_confidence.low} low confidence. Detection is read-only; merging always requires a preview and typed confirmation.
            {!execEnabled && (
              <span className="block text-amber-700 font-medium mt-0.5" data-testid="merge-execution-disabled-banner">
                Merge execution is disabled on this environment — scanning, review and preview only.
              </span>
            )}
          </p>
        )}
      </CardHeader>
      {open && (
        <CardContent className="p-3 sm:p-4 pt-0 space-y-3">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : groupCount === 0 ? (
            <p className="text-sm text-muted-foreground py-2" data-testid="no-duplicates-message">
              <Users className="h-4 w-4 inline mr-1" /> No duplicate candidates found.
            </p>
          ) : (
            data.groups.map((group) => (
              <div key={group.group_id} className="border rounded-lg p-3 space-y-2" data-testid={`duplicate-group-${group.group_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={`border ${CONF_STYLES[group.confidence]}`} data-testid={`group-confidence-${group.group_id}`}>
                    {group.confidence.toUpperCase()} confidence
                  </Badge>
                  <span className="text-xs text-muted-foreground">{group.customers.length} records</span>
                </div>
                <ul className="text-xs text-muted-foreground list-disc pl-4">
                  {group.pairs.flatMap(p => p.reasons).filter((r, i, a) => a.indexOf(r) === i).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {group.customers.map(c => <CustomerColumn key={c.id} c={c} />)}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={() => openMerge(group)} data-testid={`preview-merge-btn-${group.group_id}`}>
                    Preview merge
                  </Button>
                  {group.pairs.map((p, i) => (
                    <Button key={i} size="sm" variant="outline" onClick={() => handleIgnore(p)} disabled={!execEnabled}
                      title={!execEnabled ? 'Merge execution is disabled on this environment — decisions cannot be saved.' : undefined}
                      data-testid={`not-duplicate-btn-${p.ids[0]}-${p.ids[1]}`}>
                      Not a duplicate{group.pairs.length > 1 ? ` (${i + 1})` : ''}
                    </Button>
                  ))}
                </div>
              </div>
            ))
          )}

          {showHistory && (
            <div className="border-t pt-3 space-y-2" data-testid="merge-history-section">
              <p className="text-sm font-semibold flex items-center gap-1"><History className="h-4 w-4" /> Merge History</p>
              {!history || history.length === 0 ? (
                <p className="text-xs text-muted-foreground">No merges recorded.</p>
              ) : history.map(m => (
                <div key={m.id} className="border rounded-md p-2 text-xs flex flex-wrap items-center justify-between gap-2" data-testid={`merge-history-${m.id}`}>
                  <div>
                    <p className="font-medium">{m.retired_names?.filter(Boolean).join(', ') || m.retired_ids.length + ' record(s)'} → {m.survivor_name || m.survivor_id}</p>
                    <div className="text-muted-foreground">{m.merged_at?.slice(0, 16).replace('T', ' ')} · build {m.app_build_version} {m.rolled_back && <Badge variant="secondary" className="ml-1">rolled back</Badge>}</div>
                  </div>
                  {!m.rolled_back && (
                    <Button size="sm" variant="outline" onClick={() => openRollback(m.id)} data-testid={`rollback-btn-${m.id}`}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Roll back
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* ---- Merge review dialog: Candidate → Review → Preview → Confirm → Merge ---- */}
      <Dialog open={!!mergeGroup} onOpenChange={(o) => !o && setMergeGroup(null)}>
        <DialogContent className="max-w-2xl mx-4 max-h-[85vh] overflow-y-auto" data-testid="merge-review-dialog">
          <DialogHeader>
            <DialogTitle>Review &amp; Merge Duplicates</DialogTitle>
            <DialogDescription>
              Nothing is written until you generate a preview AND type the confirmation phrase. Retired records are kept for audit and rollback — never deleted.
            </DialogDescription>
          </DialogHeader>
          {mergeGroup && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold mb-1">1. Choose the surviving record — untick anyone who should NOT be merged</p>
                {mergeGroup.customers.map(c => (
                  <div key={c.id} className={`flex items-start gap-2 border rounded-md p-2 mb-1 text-sm ${includedIds.includes(c.id) ? '' : 'opacity-50'}`}>
                    <Checkbox className="mt-1" checked={includedIds.includes(c.id)}
                      disabled={c.id === survivorId}
                      onCheckedChange={(v) => {
                        setIncludedIds(prev => v ? [...prev, c.id] : prev.filter(x => x !== c.id));
                        invalidatePreview();
                      }}
                      data-testid={`include-checkbox-${c.id}`} aria-label={`Include ${c.name || c.id} in the merge`} />
                    <label className="flex items-start gap-2 cursor-pointer min-w-0 flex-1">
                      <input type="radio" name="survivor" className="mt-1" checked={survivorId === c.id}
                        disabled={!includedIds.includes(c.id)}
                        onChange={() => { setSurvivorId(c.id); invalidatePreview(); }}
                        data-testid={`survivor-radio-${c.id}`} aria-label={`Keep ${c.name || c.id}`} />
                      <span className="min-w-0">
                        <span className="font-medium">{c.name || '—'} {survivorId === c.id && <Badge variant="secondary" className="ml-1 text-[10px]">survivor</Badge>}</span>
                        <span className="block text-xs text-muted-foreground truncate">
                          {c.email || 'no email'} · {c.phone || 'no phone'} · {c.embedded_jobs} jobs, {c.linked_records.invoices} invoices, {c.linked_records.payments} payments
                        </span>
                      </span>
                    </label>
                  </div>
                ))}
                {includedIds.length < 2 && (
                  <p className="text-xs text-amber-700">Select at least two records to merge.</p>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold mb-1">2. Choose which details to keep</p>
                {Object.keys(FIELD_LABELS).map(field => {
                  const values = distinctValues(field);
                  if (values.length <= 1) return null;
                  return (
                    <div key={field} className="mb-2">
                      <p className="text-xs text-muted-foreground mb-1">{FIELD_LABELS[field]}</p>
                      <div className="flex flex-wrap gap-1">
                        {values.map(v => (
                          <button key={v} type="button"
                            onClick={() => { setChoices(p => ({ ...p, [field]: v })); invalidatePreview(); }}
                            className={`text-xs border rounded-full px-3 py-1 ${((choices[field] ?? (mergeGroup.customers.find(c => c.id === survivorId)?.[field] || '').trim()) === v) ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}
                            data-testid={`choose-${field}-${v.replace(/\W+/g, '-').slice(0, 24)}`}>
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <label className="flex items-center gap-2 text-xs mt-1 cursor-pointer">
                  <Checkbox checked={preserveAlternates} onCheckedChange={(v) => { setPreserveAlternates(!!v); invalidatePreview(); }} data-testid="preserve-alternates-checkbox" />
                  Keep the other names/emails/phones/addresses as searchable aliases
                </label>
              </div>

              <Button onClick={generatePreview} disabled={busy} variant="outline" className="w-full" data-testid="generate-preview-btn">
                {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                3. Generate preview (no changes made)
              </Button>

              {preview && (
                <div className="border rounded-lg p-3 space-y-2 text-sm bg-muted/30" data-testid="merge-preview-panel">
                  <p className="font-semibold">Preview — what will happen</p>
                  <p className="text-xs">Survivor: <span className="font-mono">{preview.survivor.id}</span> ({preview.survivor.name})</p>
                  <p className="text-xs">Retiring: {preview.duplicates_to_retire.map(d => d.name || d.id).join(', ')}</p>
                  <p className="text-xs">
                    Moving to survivor: {preview.records_moving_counts.enquiries} enquiries, {preview.records_moving_counts.invoices} invoices, {preview.records_moving_counts.payments} payments, {preview.records_moving_counts.blocked_days} calendar entries, {preview.records_moving_counts.recurring_contracts} contracts, {preview.embedded_jobs_moving} embedded jobs.
                  </p>
                  <p className="text-xs">Active customers: {preview.counts.active_customers_before} → {preview.counts.active_customers_after}</p>
                  {preview.conflicts.length > 0 && (
                    <div className="text-xs">
                      <p className="font-medium">Conflicting values (your selection wins):</p>
                      <ul className="list-disc pl-4">
                        {preview.conflicts.map(c => <li key={c.field}>{FIELD_LABELS[c.field]}: {c.values.join(' / ')} → keeping “{c.selected || '—'}”</li>)}
                      </ul>
                    </div>
                  )}
                  {preview.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-700 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {w}</p>
                  ))}
                  <div className="pt-1">
                    {execEnabled ? (
                      <>
                        <label className="text-xs font-medium" htmlFor="merge-confirm-input">
                          4. Type <span className="font-mono bg-muted px-1">MERGE CUSTOMERS</span> to enable the merge
                        </label>
                        <Input id="merge-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="MERGE CUSTOMERS" className="mt-1" data-testid="merge-confirm-input" />
                        <Button onClick={executeMerge} disabled={busy || confirmText !== 'MERGE CUSTOMERS'} className="w-full mt-2" data-testid="execute-merge-btn">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          Merge now (reversible)
                        </Button>
                      </>
                    ) : (
                      <p className="text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded-md p-2" data-testid="merge-execution-disabled-note">
                        Merge execution is disabled on this environment. This preview made no changes — no merge can be performed until execution is enabled server-side.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Rollback dialog ---- */}
      <Dialog open={!!rollbackPlan} onOpenChange={(o) => !o && setRollbackPlan(null)}>
        <DialogContent className="max-w-lg mx-4" data-testid="rollback-dialog">
          <DialogHeader>
            <DialogTitle>Roll Back Merge</DialogTitle>
            <DialogDescription>Restores the retired customers and their records. Payments and amounts are never altered.</DialogDescription>
          </DialogHeader>
          {rollbackPlan && (
            <div className="space-y-2 text-sm">
              {rollbackPlan.checks_passed.map((c, i) => <p key={i} className="text-xs text-emerald-700">✓ {c}</p>)}
              {rollbackPlan.blockers.map((b, i) => <p key={i} className="text-xs text-red-700 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {b}</p>)}
              {!rollbackPlan.safe ? (
                <p className="text-xs text-muted-foreground" data-testid="rollback-unsafe-message">
                  Automatic rollback is unsafe because related records changed after the merge. Resolve manually or restore from the database backup.
                </p>
              ) : !execEnabled ? (
                <p className="text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded-md p-2" data-testid="rollback-execution-disabled-note">
                  Merge/rollback execution is disabled on this environment. This safety preview made no changes.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">{rollbackPlan.note}</p>
                  <label className="text-xs font-medium" htmlFor="rollback-confirm-input">
                    Type <span className="font-mono bg-muted px-1">ROLLBACK MERGE</span> to confirm
                  </label>
                  <Input id="rollback-confirm-input" value={rollbackConfirm} onChange={(e) => setRollbackConfirm(e.target.value)}
                    placeholder="ROLLBACK MERGE" data-testid="rollback-confirm-input" />
                  <Button onClick={executeRollback} disabled={busy || rollbackConfirm !== 'ROLLBACK MERGE'} className="w-full" data-testid="execute-rollback-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Roll back this merge
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
